// The one POST /product/create that is currently in flight, if any. Every field in
// the onboarding stepper decides "create or update" by looking at entityId === null,
// so two quick changes both see null and both used to create an ad. The second call
// now joins the running create instead.
let inflightCreate = null

// Attributes the user changed while the create was in flight: they are in the local
// state but were not in the posted body, so the server's response does not know them.
function inFlightAttributeChanges (postedAttributes, currentAttributes) {
  const changes = []

  Object.keys(currentAttributes || {}).forEach((access) => {
    const current = currentAttributes[access]
    if (!Array.isArray(current)) {
      return
    }
    const posted = Array.isArray(postedAttributes?.[access]) ? postedAttributes[access] : []

    current.forEach((entry) => {
      const before = posted.find(p => p.attributeId === entry.attributeId)
      if (JSON.stringify(before?.value) !== JSON.stringify(entry.value)) {
        changes.push({
          access,
          attributeCode: entry.attributeCode,
          attributeId: entry.attributeId,
          value: entry.value
        })
      }
    })
  })

  return changes
}

function applyAttributeChanges (productAttributes, changes) {
  changes.forEach(({ access, attributeCode, attributeId, value }) => {
    // console.log('applyAttributeChanges', access, attributeCode, attributeId, value)
    if (!Array.isArray(productAttributes[access])) {
      productAttributes[access] = []
    }
    const found = productAttributes[access].find(el => el.attributeId === attributeId)
    if (found) {
      found.value = value
    } else {
      productAttributes[access].push({ attributeCode, attributeId, value })
    }
  })
}

async function executeCreateProduct (store, {
  dispatch,
  commit,
  state
}, {
  silent = false,
  source,
  sourceUrl,
  funnel
}) {
  commit('prepareProductForUpload')
  const data = state.product
  const postedAttributes = JSON.parse(JSON.stringify(data.attributes || {}))

  const url = store.$auth.loggedIn ? '/private/product/create' : '/public/product/create'

  const response = await store.$axios.post(url, data, { withCredentials: true })

  if (silent === true) {
    console.log('Silent mode, not setting product')
  } else {
    console.log('Setting product')
    // setProduct replaces the whole product, which would drop a value chosen while
    // the create was in flight; carry it over and push it to the new ad
    const changes = inFlightAttributeChanges(postedAttributes, state.product.attributes)
    applyAttributeChanges(response.data.attributes, changes)
    commit('setProduct', response.data)

    if (changes.length > 0) {
      try {
        await dispatch('updateProduct', { entityId: response.data.entityId })
      } catch (e) {
        // the value is still in the local state, the next update carries it
        console.error(e)
      }
    }
  }

  try {
    await store.$axios.put('/public/product/track/' + response.data.entityId, {
      source,
      source_url: sourceUrl,
      funnel
    }, { withCredentials: true })

    await store.$gtm.push({
      event: 'create_ad',
      auctionId: response.data.auction.auctionId,
      productId: response.data.entityId,
      transaction_id: response.data.auction.auctionId
    })
  } catch (e) {
    console.error(e)
  }

  return response
}

export default {

  getProductTypeOptionIds ({ commit }) {
    return this.$axios.get('/public/attributes/get/product/de/product_type').then((response) => {
      const motorhome = response.data.options.find(option => option.label.includes('Wohnmobil'))
      const trailer = response.data.options.find(option => option.label.includes('Wohnwagen'))
      if (motorhome && trailer) {
        commit('setProductTypeOptionIds', {
          motorhome: motorhome.optionId,
          trailer: trailer.optionId
        })
      }
    })
  },

  getEmptyProduct ({ commit }) {
    return this.$axios.get('/public/product/get/empty').then((response) => {
      commit('setProduct', response.data)
    }
    )
  },

  getEmptyDamageDto ({ commit }) {
    return this.$axios.get('/public/product/damage/get/empty').then((response) => {
      commit('setEmptyDamageDto', response.data)
    })
  },

  getProduct ({ commit }, id) {
    const storeCode = this.app.i18n.locale

    const url = this.$auth.loggedIn ? `/private/product/get/${id}?storeCode=${storeCode}` : `/public/product/get/${id}?storeCode=${storeCode}`

    return this.$axios.get(url, { withCredentials: true }).then((response) => {
      commit('setProduct', response.data)
      return response
    })
  },

  getProductsSubmittedCount ({ commit }, id) {
    const url = '/public/product/submitted-count'

    return this.$axios.get(url, { withCredentials: true }).then((response) => {
      commit('setProductsSubmittedCount', response.data)
      return response
    })
  },

  async getVersionedProduct ({ commit }, sourceEntityId) {
    const url = `/private/versioned/product/get/${sourceEntityId}`

    const { data } = await this.$axios.get(url, {
      withCredentials: true,
      params: { storeCode: this.$i18n.locale }
    })

    commit('setVersionedProduct', data)
  },

  getOwner ({ commit }, entityId) {
    return this.$axios.get('/private/customer/get/' + entityId,
      { withCredentials: true }).then((response) => {
      commit('setOwner', response.data)
    })
  },

  getOnboardingStepperData ({ commit }, {
    entityId,
    createNew = false
  }) {
    return this.$axios.get('/public/product/onboarding-stepper',
      {
        withCredentials: true,
        params: {
          entityId,
          createNew
        }
      }
    ).then((response) => {
      commit('setProduct', response.data.product)

      return response.data
    })
  },

  async updateProduct ({
    commit,
    state,
    dispatch
  }, {
    entityId,
    source,
    attributeCode,
    value
  }) {
    commit('prepareProductForUpload')
    commit('setLoggingPayload', {
      source,
      attributeCode,
      value
    })

    const product = state.product
    commit('setGallery', { gallery: product.gallery.filter(g => g.value) })

    const storeCode = this.app.i18n.locale
    const url = this.$auth.loggedIn ? `/private/product/update/${entityId}?storeCode=${storeCode}` : `/public/product/update/${entityId}?storeCode=${storeCode}`

    // if already waiting for update, it is safe to skip this one
    // the next put request will trigger the update, with the current (latest) data
    if (state.waitingForCallingUpdate === true) {
      return
    }
    // while updating right now, set a flag to "queue" the next put request after the running request has finished
    if (state.updating === true) {
      // console.log('waiting for update to finish')
      commit('setWaitingForCallingUpdate', true)
      return
    }

    commit('setUpdating', true)

    try {
      const { data } = await this.$axios.put(url, product, { withCredentials: true })

      commit('setUpdating', false)
      if (state.waitingForCallingUpdate === true) {
        commit('setWaitingForCallingUpdate', false)
        dispatch('updateProduct', {
          entityId,
          source,
          attributeCode,
          value
        })
      } else {
        commit('setProduct', data)
      }
    } catch (e) {
      console.error(e)
      commit('setUpdating', false)
      commit('setWaitingForCallingUpdate', false)
      throw e
    }
  },

  async saveImages (
    {
      commit,
      state,
      dispatch
    },
    {
      source,
      images
    }
  ) {
    commit('prepareProductForUpload')
    const gallery = state.product.gallery
      .filter(image => image.value !== null)
      .concat(images)
    commit('setLoggingPayload', {
      source,
      attributeCode: 'gallery',
      value: gallery
    })

    if (state.waitingForCallingUpdate) {
      return
    }

    if (state.updating) {
      commit('setWaitingForCallingUpdate', true)

      return
    }

    commit('setUpdating', true)
    const productUpdateUrl = this.$auth.loggedIn
      ? `/private/product/update/${state.product.entityId}`
      : `/public/product/update/${state.product.entityId}`
    const request = {
      url: productUpdateUrl,
      params: { storeCode: this.app.i18n.locale }
    }

    try {
      const { data } = await this.$axios.put(
        request.url,
        {
          ...state.product,
          gallery
        },
        {
          params: request.params,
          withCredentials: true
        }
      )
      commit('setUpdating', false)

      if (state.waitingForCallingUpdate) {
        commit('setWaitingForCallingUpdate', false)
        dispatch('saveImages', {
          source,
          images
        })

        return
      }

      commit('setProduct', data)

      await dispatch('postGetConditionallyRequiredAttributes')
    } catch (e) {
      console.error(e)
      commit('setUpdating', false)
      commit('setWaitingForCallingUpdate', false)
      throw e
    }
  },

  async deleteAttributes ({
    commit,
    dispatch,
    state
  }, {
    entityId,
    attributeCodes
  }) {
    await (attributeCodes.map(async (attributeCode) => {
      await dispatch('deleteAttributeByCode', {
        entityId,
        attributeCode
      })
    }))
  },

  async deleteAttributeAndGetProduct ({
    commit,
    dispatch,
    state
  }, {
    entityId,
    attributeId,
    attributeCode
  }) {
    // console.log('deleteAttributeAndGetProduct', { entityId, attributeId, attributeCode })
    if (attributeId) {
      await dispatch('deleteAttributeById', {
        entityId,
        attributeId
      })
    } else if (attributeCode) {
      await dispatch('deleteAttributeByCode', {
        entityId,
        attributeCode
      })
    }

    await dispatch('getProduct', entityId)
  },

  async deleteAttributeById ({
    commit,
    state
  }, {
    entityId,
    attributeId
  }) {
    const url = this.$auth.loggedIn
      ? `/private/product/delete-by-attribute-id/${entityId}/${attributeId}`
      : `/public/product/delete-by-attribute-id/${entityId}/${attributeId}`

    await this.$axios.delete(url, { withCredentials: true })
  },

  async deleteAttributeByCode ({
    commit,
    state
  }, {
    entityId,
    attributeCode
  }) {
    const url = this.$auth.loggedIn
      ? `/private/product/delete-by-attribute-code/${entityId}/${attributeCode}`
      : `/public/product/delete-by-attribute-code/${entityId}/${attributeCode}`

    await this.$axios.delete(url, { withCredentials: true })
  },

  async deleteVersionedAttributeByCode ({
    commit,
    state
  }, {
    entityId,
    attributeCode
  }) {
    const url = `/private/versioned/product/delete-by-attribute-code/${entityId}/${attributeCode}`

    await this.$axios.delete(url, { withCredentials: true })
  },

  async deleteAttributeAndGetVersionedProduct ({
    commit,
    dispatch,
    state
  }, {
    entityId,
    attributeCode,
    sourceEntityId
  }) {
    await dispatch('deleteVersionedAttributeByCode', {
      entityId,
      attributeCode
    })

    await dispatch('getVersionedProduct', sourceEntityId)
  },

  async updateVersionedProduct ({
    commit,
    state
  }, { entityId }) {
    commit('prepareVersionedProductForUpload')

    const versionedProduct = state.versionedProduct
    commit('setVersionedProductGallery', { gallery: versionedProduct.gallery.filter(g => g.value) })

    const url = `/private/versioned/product/update/${entityId}`

    const { data } = await this.$axios.put(url, versionedProduct,
      {
        withCredentials: true,
        params: { storeCode: this.$i18n.locale }
      })

    commit('setVersionedProduct', data)
  },

  async updateAndGetProduct ({
    dispatch,
    state,
    rootState
  }, {
    entityId,
    source,
    attributeCode,
    value
  }) {
    if (!this.$auth.loggedIn) {
      // not logged in but customer id (owner) is the same as the product owner
      await dispatch('customer/getOwnerSegmentation', { entityId }, { root: true })

      if (state.product.customerId === rootState.customer.customer.customerId &&
        rootState.customer.customer?.segmentation === 'prospect') {
        // console.log('not logged in but customer id (owner) is the same as the product owner')
        await dispatch('updateProduct', {
          entityId,
          source,
          attributeCode,
          value
        })
        await dispatch('postGetConditionallyRequiredAttributes')
      } else {
        await dispatch('setOverlay', true)
      }
    } else {
      // console.log('logged in')
      await dispatch('updateProduct', {
        entityId,
        source,
        attributeCode,
        value
      })
      await dispatch('postGetConditionallyRequiredAttributes')
    }

    try {
      dispatch('useractionlog/collect', {
        action: 'update_ad',
        productEntityId: entityId
      }, { root: true })
    } catch (e) {
      console.error('Error collecting user action log:', e)
    }
  },

  // Resolves with the axios response ({ status, data }). A call made while another
  // create is still running gets that create's result plus joinedInFlightCreate: true,
  // so the caller can skip its own "ad created" bookkeeping.
  async createProduct (context, payload) {
    if (inflightCreate) {
      const { status, data } = await inflightCreate
      return { status, data, joinedInFlightCreate: true }
    }

    inflightCreate = executeCreateProduct(this, context, payload)
    try {
      return await inflightCreate
    } finally {
      inflightCreate = null
    }
  },

  async getAttributes ({ commit }) {
    const storeCode = this.app.i18n.locale

    const { data } = await this.$axios.get(`/public/attributes/all/product,auction,damage/${storeCode}`)
    commit('setAttributes', data)
  },

  async getAttributesDe ({ commit }) {
    const { data } = await this.$axios.get('/public/attributes/all/product/de')
    commit('setAttributesDe', data)

    const motorhome = data.product_type?.options.find(option => option.label.includes('Wohnmobil'))
    const trailer = data.product_type?.options.find(option => option.label.includes('Wohnwagen'))
    if (motorhome && trailer) {
      commit('setProductTypeOptionIds', {
        motorhome: motorhome.optionId,
        trailer: trailer.optionId
      })
    }
  },

  async postGetConditionallyRequiredAttributes ({
    commit,
    state
  }) {
    const storeCode = this.app.i18n.locale
    // axios only sets a JSON content type when the body is an object, so an empty
    // product would be posted as x-www-form-urlencoded and rejected with a 415
    if (typeof state.product === 'object' && state.product !== null) {
      const { data } = await this.$axios.post(`/public/attributes/all/product,auction,damage/${storeCode}`, state.product, {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
      })

      commit('setAttributes', data)
    }
  },

  setSimpleAttribute ({ commit }, {
    attributeCode,
    value
  }) {
    commit('setSimpleAttribute', {
      attributeCode,
      value
    })
  },

  setSimpleAuctionAttribute ({ commit }, {
    attributeCode,
    value
  }) {
    commit('setSimpleAuctionAttribute', {
      attributeCode,
      value
    })
  },

  setAttribute ({ commit }, {
    attributeCode,
    value
  }) {
    commit('setAttribute', {
      attributeCode,
      value
    })
  },

  setVersionedProductAttribute ({ commit }, {
    attributeCode,
    value
  }) {
    commit('setVersionedProductAttribute', {
      attributeCode,
      value
    })
  },

  addToGallery ({
    state,
    commit
  }, image) {
    commit('addToGallery', image)

    const imageIndex = state.product.gallery.length - 1

    return imageIndex
  },

  addToVersionedProductGallery ({
    state,
    commit
  }, image) {
    commit('addToVersionedProductGallery', image)

    const imageIndex = state.versionedProduct.gallery.length - 1

    return imageIndex
  },

  updateGallery ({
    commit,
    dispatch
  }, array) {
    if (array.length > 0) {
      commit('updateGallery', array)
      dispatch('setAttribute', {
        attributeCode: 'image',
        value: array[0].value
      })
    }
  },

  removeGalleryImage ({ commit }, index) {
    commit('removeGalleryImage', index)
  },

  removeVersionedProductGalleryImage ({ commit }, index) {
    commit('removeVersionedProductGalleryImage', index)
  },

  removeLastGalleryImage ({ commit }) {
    commit('removeLastGalleryImage')
  },
  removeLastGalleryImages ({ commit }, count) {
    commit('removeLastGalleryImages', count)
  },

  removeLastVersionedProductGalleryImages ({ commit }, count) {
    commit('removeLastVersionedProductGalleryImages', count)
  },

  updateGalleryImage ({ commit }, {
    index,
    value
  }) {
    commit('updateGalleryImage', {
      index,
      value
    })
  },

  updateVersionedProductGalleryImage ({ commit }, {
    index,
    value
  }) {
    commit('updateVersionedProductGalleryImage', {
      index,
      value
    })
  },

  updateDamages ({ commit }, {
    sortOrder,
    attributeCode,
    value
  }) {
    commit('updateDamages', {
      sortOrder,
      attributeCode,
      value
    })
  },

  removeDamageImage ({ commit }, { sortOrder }) {
    commit('removeDamageImage', { sortOrder })
  },

  removeAllDamageImages ({ commit }) {
    commit('removeAllDamageImages')
  },

  setOverlay ({ commit }, value) {
    commit('setOverlay', value)
  },

  closePanels ({ commit }) {
    commit('closePanels')
  },

  closePanel ({ commit }, idx) {
    commit('closePanel', idx)
  },

  closePanelEquipment ({ commit }, idx) {
    commit('closePanelEquipment', idx)
  }

}
